<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CatalogAPI from 'dashboard/api/catalog';

const props = defineProps({
  orderItems: {
    type: Array,
    default: null,
  },
  currency: {
    type: String,
    default: 'BRL',
  },
  savedItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:items']);

const { t } = useI18n();
const { localeTag } = useDateLocale();

const allProducts = ref([]);
const loading = ref(false);
const loadingMore = ref(false);
const searchQuery = ref('');
const searchQuerySent = ref('');
const showDropdown = ref(false);
const currentPage = ref(1);
const hasMore = ref(false);
const searchDebounce = ref(null);
const searchInputRef = ref(null);

const hasOrder = computed(() => !!props.orderItems?.length);

const items = computed(() => {
  if (props.savedItems?.length) return props.savedItems;
  return [];
});

const catalogProducts = computed(() => {
  if (hasOrder.value) {
    return (props.orderItems || []).map(item => ({
      id: item.id,
      title: item.title,
      variant_title: item.variant_title,
      image_url: item.image_url,
      sku: item.sku,
      price: parseFloat(item.unit_price),
      catalog_variant_id: item.catalog_variant_id || null,
    }));
  }
  return allProducts.value.flatMap(p =>
    (p.variants || [{}]).map(v => ({
      id: v.id,
      title: p.title,
      variant_title: p.variants.length === 1 ? null : v.title,
      image_url: v.image_url || p.image_url,
      sku: v.sku,
      price: parseFloat(v.price || 0),
      catalog_variant_id: v.id,
    }))
  );
});

const fetchProducts = async (query = '', page = 1, append = false) => {
  if (hasOrder.value) return;
  try {
    if (page === 1) loading.value = true;
    else loadingMore.value = true;

    const response = await CatalogAPI.getProducts(query, page);
    const newProducts = response.data.products || [];

    if (append) {
      allProducts.value = [...allProducts.value, ...newProducts];
    } else {
      allProducts.value = newProducts;
    }

    currentPage.value = response.data.page || page;
    hasMore.value = response.data.has_more || false;
  } catch {
    if (!append) allProducts.value = [];
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

watch(
  () => hasOrder.value,
  val => {
    if (!val && allProducts.value.length === 0) fetchProducts();
  },
  { immediate: true }
);

watch(showDropdown, val => {
  if (val && !hasOrder.value) {
    nextTick(() => searchInputRef.value?.focus());
  }
});

const onSearchInput = event => {
  const query = event.target.value;
  searchQuery.value = query;
  clearTimeout(searchDebounce.value);
  searchDebounce.value = setTimeout(() => {
    searchQuerySent.value = query;
    fetchProducts(query, 1, false);
  }, 300);
};

const onScroll = event => {
  if (hasOrder.value) return;
  const el = event.target;
  if (
    el.scrollTop + el.clientHeight >= el.scrollHeight - 20 &&
    hasMore.value &&
    !loadingMore.value
  ) {
    fetchProducts(searchQuerySent.value, currentPage.value + 1, true);
  }
};

const formatCurrency = amount => {
  return new Intl.NumberFormat(localeTag.value, {
    style: 'currency',
    currency: props.currency || 'BRL',
  }).format(amount);
};

const thumbUrl = url => {
  if (!url) return url;
  return url.includes('?') ? `${url}&width=80` : url;
};

const addItem = product => {
  const matchKey = p =>
    p.catalog_variant_id
      ? `${p.catalog_variant_id}`
      : `${p.title}|${p.variant_title || ''}`;

  const exists = items.value.find(i => matchKey(i) === matchKey(product));
  if (exists) {
    const updated = items.value.map(i =>
      matchKey(i) === matchKey(product) ? { ...i, quantity: i.quantity + 1 } : i
    );
    emit('update:items', updated);
    showDropdown.value = false;
    return;
  }
  emit('update:items', [
    ...items.value,
    {
      title: product.title,
      variant_title: product.variant_title || null,
      sku: product.sku || null,
      image_url: product.image_url,
      quantity: 1,
      unit_price: product.price,
      catalog_variant_id: product.catalog_variant_id || null,
    },
  ]);
  showDropdown.value = false;
};

const removeItem = index => {
  const updated = [...items.value];
  updated.splice(index, 1);
  emit('update:items', updated);
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      v-for="(item, idx) in items"
      :key="idx"
      class="flex items-center gap-3 px-3 py-2 rounded-lg border border-n-weak"
    >
      <img
        v-if="item.image_url"
        :src="thumbUrl(item.image_url)"
        :alt="item.title"
        class="w-10 h-10 rounded object-cover shrink-0"
      />
      <div
        v-else
        class="w-10 h-10 rounded bg-n-solid-3 flex items-center justify-center shrink-0"
      >
        <span class="i-lucide-image text-n-slate-10 text-sm" />
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm text-n-slate-12 truncate">{{ item.title }}</p>
        <p v-if="item.variant_title" class="text-xs text-n-slate-11 truncate">
          {{ item.variant_title }}
        </p>
      </div>
      <div class="flex items-center gap-1.5 shrink-0">
        <span class="text-xs text-n-slate-11">
          {{ t('KANBAN.PRODUCTS.LINE_ITEM.QTY_VALUE', { qty: item.quantity }) }}
        </span>
        <span class="text-sm font-medium text-n-slate-12">
          {{ formatCurrency(item.unit_price * item.quantity) }}
        </span>
      </div>
      <button
        class="shrink-0 text-n-slate-10 hover:text-n-ruby-11"
        @click="removeItem(idx)"
      >
        <span class="i-lucide-x size-4" />
      </button>
    </div>

    <OnClickOutside @trigger="showDropdown = false">
      <div class="relative">
        <button
          class="flex items-center gap-1 px-2 py-1 rounded-md outline-dashed h-6 outline-1 outline-n-slate-6 hover:bg-n-alpha-2"
          :class="{ 'bg-n-alpha-2': showDropdown }"
          @click="showDropdown = !showDropdown"
        >
          <span class="i-lucide-plus" />
          <span class="text-sm text-n-slate-11">
            {{ t('KANBAN.PRODUCTS.ADD_LINE_ITEM') }}
          </span>
        </button>

        <div
          v-if="showDropdown"
          class="absolute z-[100] w-80 mt-2 ltr:left-0 rtl:right-0 top-full rounded-lg border border-n-weak bg-n-solid-1 shadow-lg"
        >
          <div v-if="!hasOrder" class="p-2 border-b border-n-weak">
            <input
              ref="searchInputRef"
              :value="searchQuery"
              :placeholder="t('KANBAN.PRODUCTS.SEARCH_PLACEHOLDER')"
              class="w-full text-sm px-2 py-1.5 rounded border border-n-weak outline-none focus:outline-n-brand bg-transparent"
              @input="onSearchInput"
            />
          </div>

          <div class="overflow-y-auto max-h-52" @scroll="onScroll">
            <div
              v-if="loading && catalogProducts.length === 0"
              class="flex items-center justify-center py-6"
            >
              <Spinner />
            </div>

            <div
              v-else-if="catalogProducts.length === 0"
              class="px-3 py-4 text-sm text-n-slate-11 text-center"
            >
              {{
                hasOrder
                  ? t('KANBAN.PRODUCTS.NO_ITEMS')
                  : t('KANBAN.PRODUCTS.NO_RESULTS')
              }}
            </div>

            <template v-else>
              <button
                v-for="p in catalogProducts"
                :key="`${p.id}-${p.catalog_variant_id}`"
                class="w-full flex items-center gap-2 px-3 py-2 hover:bg-n-alpha-2 text-left"
                @click="addItem(p)"
              >
                <img
                  v-if="p.image_url"
                  :src="thumbUrl(p.image_url)"
                  :alt="p.title"
                  class="size-8 rounded object-cover shrink-0"
                />
                <div
                  v-else
                  class="size-8 rounded bg-n-solid-3 flex items-center justify-center shrink-0"
                >
                  <span class="i-lucide-image text-n-slate-10 text-xs" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm text-n-slate-12 truncate">{{ p.title }}</p>
                  <p
                    v-if="p.variant_title"
                    v-tooltip="p.variant_title"
                    class="text-xs text-n-slate-11 truncate"
                  >
                    {{ p.variant_title }}
                  </p>
                </div>
                <span class="text-xs font-medium text-n-slate-11 shrink-0">
                  {{ formatCurrency(p.price) }}
                </span>
              </button>
            </template>

            <div
              v-if="loadingMore"
              class="flex items-center justify-center py-2"
            >
              <Spinner />
            </div>
          </div>
        </div>
      </div>
    </OnClickOutside>
  </div>
</template>
